/* eslint-disable react/jsx-fragments */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Grid, Header } from 'semantic-ui-react';
import { ExposureTable, ExposureChart } from '../../Analytics/components';
import AssetLegend from '../../../../components/AssetLegend';

@connect((state) => ({
  french: state.Storage.language === 'fr',
  partnerName: state.Storage['partner-name'],
}))
export default class AssetClass extends Component {
  render() {
    const { french, analytics, partnerName } = this.props;
    const { data } = analytics;

    const { assetAllocationByRegion = {} } = data;
    const regionGroups = Object.entries(assetAllocationByRegion).reduce(
      (acc, [key, value]) => {
        const [parent, ...rest] = key.split(' ');

        const parentKey = parent === 'Fixed' ? 'Fixed Income' : parent;
        let regionName = rest.join(' ') || parentKey;

        if (parent === 'Fixed' && rest[0] === 'Income') {
          regionName = rest.slice(1).join(' ') || parentKey;
        }

        if (!acc[parentKey]) acc[parentKey] = [];
        acc[parentKey].push({
          region: regionName,
          value,
        });
        return acc;
      },
      {}
    );
    const assetAllocation = Object.values(data.tickersValid).reduce(
      (total, value) => {
        if (total[value.class]) {
          total[value.class] += value.weight;
        } else {
          total[value.class] = value.weight;
        }
        return total;
      },
      {}
    );
    const assetAllocationClass = {
      Alternative:
        'Alternative' in data.assetAllocation
          ? data.assetAllocation.Alternative
          : 0,
      Equity:
        'Equity' in data.assetAllocation ? data.assetAllocation.Equity : 0,
      'Fixed Income':
        'Fixed Income' in data.assetAllocation
          ? data.assetAllocation['Fixed Income']
          : 0,
      Cash: 'Cash' in data.assetAllocation ? data.assetAllocation.Cash : 0,
      Derivatives:
        'Misc' in data.assetAllocation ? data.assetAllocation.Misc : 0,
    };
    const riskContClass = {
      Alternative:
        'Alternative' in data.riskCont.class
          ? data.riskCont.class.Alternative
          : 0,
      Equity: 'Equity' in data.riskCont.class ? data.riskCont.class.Equity : 0,
      'Fixed Income':
        'Fixed Income' in data.riskCont.class
          ? data.riskCont.class['Fixed Income']
          : 0,
      Cash: 'Cash' in data.riskCont.class ? data.riskCont.class.Cash : 0,
      'Asset Allocation':
        'Asset Allocation' in data.riskCont.class
          ? data.riskCont.class['Asset Allocation']
          : 0,
    };

    const customOrder = [
      'Alternative',
      'Equity',
      'Fixed Income',
      'Asset Allocation',
      'Cash',
    ];
    const sortedData = customOrder.map((key) => ({
      ticker: key,
      assetAllocation: assetAllocation[key] || 0,
      riskCont: riskContClass[key] || 0,
      retCont: data.retCont.class[key] || 0,
    }));
    const customSortOrder = (a, b) =>
      customOrder.indexOf(a.ticker) - customOrder.indexOf(b.ticker);

    sortedData.sort(customSortOrder);
    const filteredAssetAllocationClass = Object.entries(assetAllocationClass)
      // eslint-disable-next-line no-unused-vars
      .filter(([key, value]) => value !== 0)
      .reduce((obj, [key, value]) => {
        obj[key] = value;
        return obj;
      }, {});

    const assetAllocationValues = Object.keys(riskContClass).map((key) =>
      key in assetAllocation ? assetAllocation[key] : 0
    );

    return (
      <Container>
        <Header
          dividing
          size="large"
          content={
            french ? "Analyse de la classe d'actifs" : 'Asset Class Analysis'
          }
          style={{ marginBottom: '0.5rem' }}
        />
        <Header
          size="medium"
          content={french ? 'Contribution au risque' : 'Risk Contribution'}
          subheader={french ? "Par classe d'actifs" : 'By Asset Class'}
          style={{ marginTop: '0.5rem' }}
        />
        {french ? (
          <p>
            Si vous détenez des fonds qui investissent dans plus d’une catégorie
            d’actifs, veuillez noter que nous ne sommes pas en mesure de séparer
            la contribution au risque de leurs catégories d’actifs sous-jacentes
            (p. ex. la portion en actions d’un fonds équilibré), puisque les
            fonds ne publient pas les rendements par catégorie d’actifs
            individuellement.
          </p>
        ) : (
          <p>
            If you have funds that invest in more than one asset class, note
            that we are not able to separate the risk contribution of their
            underlying asset classes (e.g., the equity portion of a balanced
            fund), because funds do not report their asset class returns
            individually.
          </p>
        )}

        <Grid>
          <Grid.Row columns={2}>
            <Grid.Column>
              <Header
                size="medium"
                content={french ? 'Répartition des actifs' : 'Asset Allocation'}
                style={{ margin: 0 }}
              />
              <ExposureChart
                title=""
                labels={Object.keys(riskContClass)}
                data={assetAllocationValues}
                hideLegend
              />
            </Grid.Column>

            <Grid.Column>
              <Header
                size="medium"
                content={
                  french ? 'Contribution au risque' : 'Risk Contribution'
                }
                style={{ margin: 0 }}
              />
              <ExposureChart
                title=""
                labels={Object.keys(riskContClass)}
                data={Object.values(riskContClass)}
                hideLegend
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>

        <ExposureTable
          headers={[
            <div>{french ? 'Classe' : 'Class'}</div>,
            french ? 'Répartition' : 'Allocation',
            french ? 'Contribution au risque' : 'Risk Contribution',
            french ? 'Contribution au rendement' : 'Return Contribution',
          ]}
          totals={[
            french ? 'Total' : 'Total',
            <div>100%</div>,
            <div>100%</div>,
            <div>100%</div>,
          ]}
          data={sortedData}
          assetClass
          pdf
        />
        <div style={{ paddingTop: '3rem', paddingBottom: '5rem' }}>
          <Header
            size="medium"
            content={
              french
                ? 'Répartition des titres sous-jacents'
                : 'Underlying Holdings Breakdown'
            }
            subheader={
              french
                ? 'Par catégorie d’actifs, région et structure'
                : 'By Asset Class, Region, and Structure'
            }
          />
          <p style={{ marginTop: '1rem' }}>
            {french
              ? 'Si vous détenez des fonds de répartition d’actifs ou des fonds équilibrés, ce graphique tient compte de la répartition d’actifs des positions sous-jacentes. '
              : 'If you have any asset allocation or balanced funds, this chart accounts for the asset mix of the underlying holdings. '}
            {french
              ? 'Par exemple, si vous investissez 50 % de votre portefeuille dans un fonds 60/40 et 50 % dans un fonds 80/20, votre répartition nette de l’actif est alors de 70/30.'
              : 'For example, if you put 50% of your money in a 60/40 fund and 50% in an 80/20 fund, then your net asset allocation is 70/30.'}
            <br />
            <br />
            {french
              ? 'Veuillez noter que i) certains fonds d’actions peuvent détenir un faible pourcentage de titres à revenu fixe à court terme ou de produits dérivés, et ii) les FNB synthétiques afficheront 100 % de produits dérivés, puisque leurs titres sous-jacents sont des contrats de swap.'
              : 'Note that i) some equity funds may have a small percentage of short-term fixed income securities or derivatives, and ii) synthetic ETFs will show 100% derivatives as their underlying holdings are swap contracts.'}
            <br />
            <br />
            {(filteredAssetAllocationClass['Fixed Income'] ||
              filteredAssetAllocationClass.Derivatives ||
              filteredAssetAllocationClass.Cash) && (
              <React.Fragment>
                {french ? 'Remarques:' : 'Notes:'}
                <br />
              </React.Fragment>
            )}
            {filteredAssetAllocationClass['Fixed Income'] && (
              // eslint-disable-next-line react/jsx-no-useless-fragment
              <React.Fragment>
                {french
                  ? 'Revenu fixe comprend l’encaisse et les quasi-espèces dans les fonds.'
                  : 'Fixed Income includes cash and cash equivalents in funds.'}
              </React.Fragment>
            )}
            {filteredAssetAllocationClass.Derivatives && (
              <React.Fragment>
                <br />
                {french
                  ? 'Les produits dérivés comprennent les contrats de swap, d’options et de contrats à terme.'
                  : 'Derivatives include swap, option, and futures contracts.'}
              </React.Fragment>
            )}
            {filteredAssetAllocationClass.Cash && (
              <React.Fragment>
                <br />
                {french
                  ? 'Encaisse correspond à l’argent comptant sans intérêt dans votre compte de placement.'
                  : 'Cash is the non-interest-bearing cash in your investment account.'}
              </React.Fragment>
            )}
          </p>
        </div>

        <Grid
          style={{
            marginTop: '1rem',
            marginBottom: '3rem',
            breakInside: 'avoid',
          }}
        >
          <Grid.Row columns={2}>
            <Grid.Column>
              <ExposureChart
                title=""
                labels={Object.keys(filteredAssetAllocationClass)}
                data={Object.values(filteredAssetAllocationClass)}
                hideLegend
              />
            </Grid.Column>
            <Grid.Column>
              <div className="ringchart-container-parent">
                <div className="table">
                  <AssetLegend
                    data={filteredAssetAllocationClass}
                    partnerName={partnerName}
                    french={french}
                    regionAssets={regionGroups}
                  />
                </div>
              </div>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Container>
    );
  }
}

AssetClass.propTypes = {
  analytics: PropTypes.object,
  french: PropTypes.bool,
  partnerName: PropTypes.string,
};

AssetClass.defaultProps = {
  analytics: {},
  french: false,
  partnerName: '',
};

const Container = styled.div`
  padding: 1rem 2rem 1rem 2rem;

  .ringchart-container-parent {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    column-gap: 1rem;
    height: 100%;
    width: 100%;
    padding-right: 10%;

    .table {
      width: 50%;
      min-width: 225px;
      .item {
        display: flex;
        flex-direction: row;
        align-items: start;
        column-gap: 5px;
      }
    }
  }
`;
